import React, { useMemo } from "react";
import { Open16Regular } from "@fluentui/react-icons";
import { createCopyOfTableContents } from "../../../../membersportal/services/TableFunctions";
import DisplayTable from "../../../../membersportal/sharedComponents/displayTable/DisplayTable";
import { SizedActionButton } from "../../../components/actionButton/ActionButton";
import { ADD_WORKS_FROM_PACKAGE_VIEW, AGREEMENTS_APPLICABLE_WORKS_SEARCH, APPLICABLE_WORKS_KEY, AVDETAILS_COUNTRY_OF_ORIGIN_STATE_KEY, SEARCH_VIEW } from "../../../Consts";
import IRepertoireComponentDataItem from "../../../../redux/types/IRepertoireComponentDataItem";
import { IAgreementWork } from "../../../types/IAgreementWork";
import { IAgreementWorkDataItems } from "../../../types/IAgreementWorkDataItems";
import { IRepertoireField } from "../../../types/IRepertoireField";
import { IRepertoireStateKeys } from "../../../types/IRepertoireStateKeys";
import { getLookupValues, mapLookupKey, getLookupDefault } from "../../../../lookup/services/LookupHelpers";
import { APPLICABLE_WORKS_LOOKUP } from "../../../../../src/lookup/Consts";
import { ResultsTable } from "../../../components/agreementComponent/ResultTable"; // Replace with the correct path to ResultsTable
import { updatePagination } from "../../../../redux/reducers/RepertoireReducer";
import { IApplicableWorkSearchQuery } from "../../../types/IApplicableWorkSearchQuery";
import { ILookupDictionary } from "../../../../lookup/types/ILookupDictionary";
import { DataSource } from "../../../types/DataSource";
import { IMyPackagesDetails } from "../../../types/IMyPackagesDetails";
import { SizedDropdownDataInput } from "../../../components/dropdownDataInput/DropdownDataInput";
import { hideModal } from "../../../../redux/reducers/ModalReducer";

export interface IAgreementMaintenanceWorkTableContainerProps {
    tableContents: IAgreementWorkDataItems[];
    dataGridTableData: IRepertoireComponentDataItem;
    componentInstance: string;
    openWork: (openItem: IAgreementWorkDataItems) => void;
    changeData: (value: any, name: IRepertoireStateKeys, objectKey?: string, ipRepresentationsIdsToRemove?: number[]) => void;
    stateKey: string;
    lookupValues: any;
    showModal: any;
    ipBaseNumber?: string;
    resetPagination: (repertoireSection: string) => void;
    setIsHundredResultsPerPage?: (isHundredResultsPerPage: boolean) => void;
    indexOfFirstResult?: number;
    indexOfLastResult?: number;
    resultsPerPage?: number;
    currentPage?: number;
    isHundredResultsPerPage?: boolean;
    continuationToken?: string;
    sortSearchResults: (name: string, results: any) => void;
    updateContentsPagination: (indexOfFirstResult: number, indexOfLastResult: number, resultsPerPage: number, currentPage: number, repertoireSection: string) => void;
    getApplicableWorkDetails?: (applicableWork: IApplicableWorkSearchQuery, isSearched: boolean) => void;
    entityID?: number;
    entitySource?: DataSource;
    agreementsallWorks?: IAgreementWorkDataItems[];
    packageDetails?: any;
    getWorksFromPackageThunk?: (packageItems?: any, dataSource?: string) => void;
    agreementWorksFromPackageJobThreshold?: number;
    hideModal: typeof hideModal;
    isReadonly?: boolean;
}

const AgreementMaintenanceWorkTableContainer: React.FC<IAgreementMaintenanceWorkTableContainerProps> = ({
    tableContents,
    dataGridTableData,
    componentInstance,
    stateKey,
    changeData,
    openWork,
    lookupValues,
    showModal,
    ipBaseNumber,
    resetPagination,
    setIsHundredResultsPerPage,
    indexOfFirstResult,
    indexOfLastResult,
    resultsPerPage,
    currentPage,
    isHundredResultsPerPage,
    continuationToken,
    sortSearchResults,
    updateContentsPagination,
    getApplicableWorkDetails,
    entityID,
    entitySource,
    agreementsallWorks,
    packageDetails,
    getWorksFromPackageThunk,
    agreementWorksFromPackageJobThreshold,
    hideModal,
    isReadonly
}) => {
    const EXCLUSION_UNTIL = 'exclusionUntil';
    const WORKS_RETAINED_UNTIL = 'worksRetainedUntil';
    const WORKS_COLLECTION_UNTIL = 'workCollectionUntil';
    const USE_DEFAULT_COLLECTION = 'useDefaultCollection';
    const [deletedRowItems, setDeletedRowItems] = React.useState<IAgreementWorkDataItems[]>([]);
    const [activeSortHeaderSection, setActiveSortHeaderSection] = React.useState<string>("");
    const [ascending, setAscending] = React.useState<boolean>(false);

    let filteredTableData = dataGridTableData.fields.filter(
        (field: IRepertoireField) => {
             return field.componentInstance === componentInstance && field.data !== 'Work'
        }
    )

    React.useEffect(() => {
        if (deletedRowItems.length === 0 && tableContents) {
            setDeletedRowItems(tableContents.filter(x => x.isDeleted === true));
        }
    }, [tableContents, deletedRowItems.length]);

    const tableContentsWithSideTrackAndSequence = React.useMemo(() => {
        if (!tableContents) return [];

        return createCopyOfTableContents(
            tableContents.filter(x => x.isDeleted === false)
        );
    }, [
        tableContents,
        dataGridTableData,
        componentInstance,
        stateKey,
        changeData,
        openWork,
        lookupValues,
        showModal,
        dataGridTableData.fields
    ]);

    const sortTableByColumn = (section: string) => {
        let tableContentsCopy = JSON.parse(JSON.stringify(tableContentsWithSideTrackAndSequence));
        if (activeSortHeaderSection !== section) {
            setAscending(false);
        }
        if (tableContentsCopy) {
            tableContentsCopy.sort((a: any, b: any) => {
                const newA = a[section] ? (a[section].value ? a[section].value : a[section]) || "" : "";
                const newB = b[section] ? (b[section].value ? b[section].value : b[section]) || "" : "";

                if (ascending) {
                    return newA.toString().localeCompare(newB.toString(), undefined, { 'numeric': true });
                }
                return newB.toString().localeCompare(newA.toString(), undefined, { 'numeric': true });
            });

            setActiveSortHeaderSection(section);
            setAscending(!ascending);
        }
    }

    const removeRowAction = (rowItem: any) => {
        const tableContentsCopy = [...tableContents];

        const index = rowItem.agreementWorkID === 0 ? tableContentsCopy.map((item, i) => ({ item, i })).filter(x => x.item.isDeleted === false)[rowItem.rowIndex]?.i : tableContentsCopy.findIndex(item => item.agreementWorkID === rowItem.agreementWorkID);

        if (index === undefined || index === -1) {
            return;
        }

        const removedItem = tableContentsCopy[index];

        if (removedItem.agreementWorkID === 0) {
            tableContentsCopy.splice(index, 1);
        } else {
            tableContentsCopy[index] = {
                ...removedItem,
                isDeleted: true,
                isChanged: true
            };
        }

        setDeletedRowItems(prevState => [...prevState, removedItem]);
        changeData(tableContentsCopy, stateKey);
    }

    const rowAction = (openItem: any) => {
        openWork(openItem)
    }

    const rowActions = {
        iconButton: {
            icon: Open16Regular,
            renderTitleAsLink: true,
            onClick: rowAction,
        }            
    }

    const onChange = (
        value: any,
        fieldName: string,
        rowIndex: number,
    ) => {
        if (isReadonly) {
            return;
        }
        const activeRowIndexes = tableContents
            .map((item, i) => ({ item, i }))
            .filter(x => x.item.isDeleted === false)
            .map(x => x.i);
        const actualIndex = activeRowIndexes[rowIndex];
        if (actualIndex === undefined) {
            return;
        }
        const tableContentsCopy = createCopyOfTableContents(tableContents);
        tableContentsCopy[actualIndex][fieldName].value = value
        tableContentsCopy[actualIndex].isChanged = true;


        changeData(tableContentsCopy, stateKey);
    };

    const lookupOptionsAndOnChangeFunctions = {
        type: {
            onChangeValue: onChange,
        },
        exclusionUntil: {
            options: getLookupValues(mapLookupKey(stateKey, EXCLUSION_UNTIL), lookupValues),
            onChangeValue: onChange,
        },
        worksRetainedUntil: {
            options: getLookupValues(mapLookupKey(stateKey, WORKS_RETAINED_UNTIL), lookupValues),
            onChangeValue: onChange,
        },
        workCollectionUntil: {
            options: getLookupValues(mapLookupKey(stateKey, WORKS_COLLECTION_UNTIL), lookupValues),
            onChangeValue: onChange,
        },
        useDefaultCollection: {
            options: getLookupValues(mapLookupKey(stateKey, USE_DEFAULT_COLLECTION), lookupValues),
            onChangeValue: onChange,
        },
        excludedUntilDate: {
            onChangeValue: onChange,
        },
        workRetentionEndDate: {
            onChangeValue: onChange,
        },
        workCollectionEndDate: {
            onChangeValue: onChange,
        },
    };

    return <div>

        <div className="tableContainer">
                <SizedActionButton
                    buttonText={"Add From Package"}
                    buttonAction={() => showModal(ADD_WORKS_FROM_PACKAGE_VIEW, null, {packageDetails: packageDetails,dataSource: entitySource, agreementId: entityID, agreementWorksFromPackageJobThreshold: agreementWorksFromPackageJobThreshold}, true, 'Select Work', '', false, 'modalSearch', null)}
                    disabled={isReadonly}
                />
            </div>
        

            <ResultsTable
                searchResultsTableData={filteredTableData}
                tableContents={tableContentsWithSideTrackAndSequence}
                componentInstance={componentInstance}
                indexOfFirstResult={indexOfFirstResult}
                indexOfLastResult={indexOfLastResult}
                resultsPerPage={resultsPerPage}
                currentPage={currentPage}
                sortSearchResults={sortSearchResults}
                changeData={undefined}
                titleContains={undefined}
                search={undefined}
                continuationToken={continuationToken}
                showFieldsModal={undefined}
                isHundredResultsPerPage={isHundredResultsPerPage}
                setIsHundredResultsPerPage={setIsHundredResultsPerPage}
                resetPagination={resetPagination}
                lookupOptionsAndOnChangeFunctions={lookupOptionsAndOnChangeFunctions}
                removeRowAction={removeRowAction}
                isLinkTitle={true}
                rowActions={rowActions}
                updateContentsTable={updateContentsPagination}
                getApplicableWorkDetails={getApplicableWorkDetails}
                entityID={entityID}
                entitySource={entitySource}
                agreementsallWorks={agreementsallWorks ? agreementsallWorks
                .filter(work => !deletedRowItems.some(d => d && d.workID === work.workID)) : []}
                isReadonly={isReadonly}
            />
            <div className="tableContainer">
                <SizedActionButton
                    buttonText={"Search"}
                    buttonAction={() => showModal(AGREEMENTS_APPLICABLE_WORKS_SEARCH, APPLICABLE_WORKS_KEY, {ipBaseNumber: ipBaseNumber}, true, 'Search', '', false, 'modalSearch', null)}
                    disabled={isReadonly}
                />
            </div>
        </div>
}

export default AgreementMaintenanceWorkTableContainer;